import { deleteManufacturerThunk, getManufacturersThunk } from '@redux/features/manufacturer.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import React, { FC, useEffect, useState } from 'react';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import AddManufacturerModal from './AddManufacturerModal';
import UpdateManufacturerModal from './UpdateManufacturerModal';

const Manufacturer: FC = () => {
    const [page, setPage] = useState<number>(0);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [manufacturerId, setManufacturerId] = useState<number>();

    const dispatch = useAppDispatch();
    const { manufacturers, loading } = useAppSelector(
        (state: RootState) => (state as any).manufacturerState || { manufacturers: [], loading: false }
    );

    const changePage = (p: number) => setPage(p);

    useEffect(() => {
        dispatch(getManufacturersThunk());
    }, [page, dispatch]);

    const handleConfirmDelete = async () => {
        if (manufacturerId) {
            dispatch(deleteManufacturerThunk(manufacturerId)).then((response: any) => {
                if (response.meta.requestStatus === 'fulfilled') {
                    setShowDeleteModal(false);
                    setManufacturerId(undefined);
                    dispatch(getManufacturersThunk());
                }
            });
        }
    };

    const handleDelete = (id: number) => {
        setManufacturerId(id);
        setShowDeleteModal(true);
    };

    const handleUpdate = (id: number) => {
        setManufacturerId(id);
        setShowUpdateModal(true);
    };

    return (
        <>
            <UpdateManufacturerModal
                show={showUpdateModal}
                handleClose={() => setShowUpdateModal(false)}
                manufacturerId={manufacturerId || 0}
            />
            <AddManufacturerModal show={showAddModal} handleClose={() => setShowAddModal(false)} />
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => {
                    setShowDeleteModal(false);
                    setManufacturerId(undefined);
                }}
                onConfirm={handleConfirmDelete}
                title="Delete Manufacturer"
                message="Are you sure you want to delete this manufacturer? This action cannot be undone."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                id={manufacturerId}
            />
            <BreadCrumb pageTitle={`Manufacturers List`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Manufacturers List</h3>
                        <ButtonBase innerText="Add Manufacturer" onClick={() => setShowAddModal(true)} />
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">DUNS Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase text-center">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(manufacturers || []).length} animationKey={`manufacturer-${page}`}>
                                            {(manufacturers || []).map((m: any) => (
                                                <AnimatedTableRow
                                                    rowKey={m?.id}
                                                    detailRecord={m}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{m?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{m?.dunsNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{m?.name || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-row justify-center items-center gap-2 w-full">
                                                            <button
                                                                className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-red-500 text-red-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-red-50 hover:border-red-600 hover:scale-105 active:scale-95"
                                                                onClick={() => {
                                                                    handleDelete(m?.id);
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                            <button
                                                                className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-yellow-600 text-yellow-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-yellow-50 hover:border-yellow-700 hover:scale-105 active:scale-95"
                                                                onClick={() => {
                                                                    handleUpdate(m?.id);
                                                                }}
                                                            >
                                                                Update
                                                            </button>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Manufacturer;
